import React, { useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, MenuItem,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { PageHeader } from '../components/ui/PageHeader';
import { getPageSubtitle } from '../constants/pageSubtitles';
import { useSystemTotal } from '../hooks/useSystemTotal';
import { activityAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { MODULE_LABELS } from '../constants/permissions';
import { brandColors, RADIUS, brandCss } from '../theme/appTheme';

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  created: { bg: brandCss.green100, color: brandCss.primaryDark },
  updated: { bg: '#e0f2fe', color: '#0369a1' },
  deleted: { bg: '#fee2e2', color: '#dc2626' },
  status_changed: { bg: '#fef3c7', color: '#d97706' },
  assigned: { bg: '#ede9fe', color: '#6d28d9' },
  imported: { bg: '#f0fdf4', color: '#15803d' },
  comment: { bg: '#f1f5f9', color: '#475569' },
};

type ActivityRow = {
  _id: string;
  moduleKey: string;
  action: string;
  summary: string;
  actorName?: string;
  createdAt: string;
};

export const ActivityLogPage: React.FC = () => {
  const { formatDate } = useLocale();
  const { total: systemTotal } = useSystemTotal('activity');
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    const params: { limit: number; moduleKey?: string } = { limit: 100 };
    if (moduleFilter !== 'all') params.moduleKey = moduleFilter;
    activityAPI.getFeed(params)
      .then((res) => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [moduleFilter]);

  const moduleOptions = Array.from(new Set(rows.map((r) => r.moduleKey))).sort();

  return (
    <Box>
      <PageHeader
        title="Change History"
        subtitle={getPageSubtitle('activity', 'Change History', { total: systemTotal })}
        icon={<HistoryIcon />}
        action={(
          <TextField
            select
            size="small"
            label="Module"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All modules</MenuItem>
            {moduleOptions.map((key) => (
              <MenuItem key={key} value={key}>
                {(MODULE_LABELS as Record<string, string>)[key] || key}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: brandCss.primary }} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No activity logged yet. Create or update records to see entries here.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const colors = ACTION_COLORS[row.action] || { bg: '#f1f5f9', color: '#475569' };
                return (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {formatDate(row.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{row.actorName || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {(MODULE_LABELS as Record<string, string>)[row.moduleKey] || row.moduleKey}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.action.replace('_', ' ')}
                        size="small"
                        sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{row.summary}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
